import { ConfigurationDocumentKind, EnumTranslatorBase, InventoryDocumentKind, LabeledEnumValue, PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { sortDocumentKinds } from './document-utils';

export enum NumerationFormat {
    Monthly = 1,
    Yearly,
    YearlyWithMonth,
    Custom,
    Manual
}

export interface NumberingsFormatType {
    readonly label: string;
    readonly value: NumerationFormat;
    readonly format?: string;
}

interface Tags {
    number: string;
    month: string;
    monthOrder: string;
    yearOrder: string;
}

const additionalSymbolPattern = /(\/[^\[\]]+[^\[\]]\/)|([^\[\]]+[^\[\]]\/)|(\/[^\[\]]+[^\[\]])/g;

export const tags: Tags = {
    number: '[Numer]',
    month: '[MM]',
    monthOrder: '[*MM]',
    yearOrder: '[*RRRR]'
};

export function getFormatSymbol(format: string): string {
    const additionalSymbolRaw = (format || '').match(additionalSymbolPattern);
    const additionalSymbol = additionalSymbolRaw?.shift() || '';
    return additionalSymbol.replace(/\//g, '');
}

export function getFormatCoreByKind(kinds: (ConfigurationDocumentKind)[]): string {
    if (kinds.includes(SaleDocumentKind.Invoice)) // Invoice & Advance invoice
        return 'FS ';
    if (kinds.includes(SaleDocumentKind.InvoiceAdjustment)) // Invoice adjustment & Advance invoice adjustment
        return 'KFS ';
    if (kinds.includes(SaleDocumentKind.ProForma))
        return 'PF ';
    if (kinds.includes(SaleDocumentKind.Receipt))
        return 'PA ';
    if (kinds.includes(SaleDocumentKind.ReceiptAdjustment))
        return 'KPA ';
    if (kinds.includes(SaleDocumentKind.VatMarginInvoice))
        return 'FM ';
    if (kinds.includes(SaleDocumentKind.VatMarginInvoiceAdjustment))
        return 'KFM ';
    if (kinds.includes(PurchaseDocumentKind.Expense))
        return 'WD ';
    if (kinds.includes(PurchaseDocumentKind.PurchaseInvoice))
        return 'FZ ';
    if (kinds.includes(PurchaseDocumentKind.PurchaseInvoiceAdjustment))
        return 'KFZ ';
    if (kinds.includes(PurchaseDocumentKind.EPurchaseInvoice))
        return 'eFZ ';
    if (kinds.includes(InventoryDocumentKind.GoodsReceipt))
        return 'PZ ';
    if (kinds.includes(InventoryDocumentKind.GoodsReceiptAdjustment))
        return 'KPZ ';
    if (kinds.includes(InventoryDocumentKind.GoodsIssue))
        return 'WZ ';
    if (kinds.includes(InventoryDocumentKind.GoodsIssueAdjustment))
        return 'KWZ ';
    if (kinds.includes(InventoryDocumentKind.InternalGoodsReceipt))
        return 'PW ';
    if (kinds.includes(InventoryDocumentKind.InternalGoodsIssue))
        return 'RW ';
    if (kinds.includes(InventoryDocumentKind.Stocktaking))
        return 'IW ';

    return '';
}

export function getFormatCore(format: string, name: (ConfigurationDocumentKind)[]): string {
    const documentType = getFormatCoreByKind(name);
    const formatSymbol = getFormatSymbol(format);
    const formatCore = `${documentType}${tags.number}${formatSymbol ? `/${formatSymbol}/` : '/'}`;
    return formatCore;
}

export function getFormatType(format: string, name: (ConfigurationDocumentKind)[]): NumerationFormat {
    const formatCore = getFormatCore(format, name);
    let formatType = NumerationFormat.Custom;
    if (`${formatCore}${tags.yearOrder}` === format) {
        formatType = NumerationFormat.Yearly;
    } else if (`${formatCore}${tags.month}/${tags.yearOrder}` === format) {
        formatType = NumerationFormat.YearlyWithMonth;
    } else if (`${formatCore}${tags.monthOrder}/${tags.yearOrder}` === format) {
        formatType = NumerationFormat.Monthly;
    }
    return formatType;
}

export function updateFormatSymbol(newSymbol: string, format: string): string | undefined {
    let finalFormat: string | undefined;
    if (newSymbol != null) {
        const oldSymbol = getFormatSymbol(format);
        if (oldSymbol) {
            finalFormat = format.replace(additionalSymbolPattern, newSymbol.length ? `/${newSymbol}/` : '/');
        } else if (newSymbol.length) {
            const number = tags.number;
            const idx = format.indexOf(number);
            finalFormat = [format.slice(0, idx + number.length), `/${newSymbol}`, format.slice(idx + number.length)].join('');
        }
    }
    return finalFormat;
}

export function getAllowedDocumentKindsForNumbering(enumTranslator: EnumTranslatorBase, options: { vatPayer: boolean, inventory: boolean }): LabeledEnumValue[] {
    const saleKinds = [
        SaleDocumentKind.Invoice,
        SaleDocumentKind.InvoiceAdjustment,
        SaleDocumentKind.ProForma,
        SaleDocumentKind.Receipt,
        SaleDocumentKind.ReceiptAdjustment
    ];

    const purchaseKinds = [
        PurchaseDocumentKind.Expense,
        PurchaseDocumentKind.PurchaseInvoice,
        PurchaseDocumentKind.PurchaseInvoiceAdjustment,
        PurchaseDocumentKind.EPurchaseInvoice
    ];

    if (options.vatPayer) {
        saleKinds.push(SaleDocumentKind.VatMarginInvoice);
        saleKinds.push(SaleDocumentKind.VatMarginInvoiceAdjustment);
    }

    const inventoryKinds = [];
    if (options.inventory) {
        inventoryKinds.push(InventoryDocumentKind.GoodsReceipt);
        inventoryKinds.push(InventoryDocumentKind.GoodsReceiptAdjustment);
        inventoryKinds.push(InventoryDocumentKind.GoodsIssue);
        inventoryKinds.push(InventoryDocumentKind.GoodsIssueAdjustment);
        inventoryKinds.push(InventoryDocumentKind.InternalGoodsReceipt);
        inventoryKinds.push(InventoryDocumentKind.InternalGoodsIssue);
        inventoryKinds.push(InventoryDocumentKind.Stocktaking);
    }
    return sortDocumentKinds([
        ...saleKinds.map(elem => enumTranslator.getLabeledEnumValue(SaleDocumentKind, elem)),
        ...purchaseKinds.map(elem => enumTranslator.getLabeledEnumValue(PurchaseDocumentKind, elem)),
        ...inventoryKinds.map(elem => enumTranslator.getLabeledEnumValue(InventoryDocumentKind, elem)),
    ], 'value');
}
